<template>
    <div :class="{ small: small }">
        <div class="filter-grid aligned-controls mb-3">
            <b-form-group v-if="!small" label="Status">
                <b-form-select :options="statusOptions" v-model="internalStatusFilter" />
            </b-form-group>
            <b-form-group v-if="!small" label="Type">
                <b-form-select :options="problemOptions" v-model="problemFilter" />
            </b-form-group>
            <b-form-group v-if="!small" label="Assignment">
                <b-form-select :options="assignmentFilterOptions" v-model="assignmentFilter" />
            </b-form-group>
            <DateRangeFilter v-model="createdDateRangeFilter" label="Created Date" />
            <b-form-group v-if="!small" label="Filter">
                <b-form-input placeholder="Patient Name / DOB / Case ID" v-model="query" />
            </b-form-group>
        </div>
        <b-row>
            <b-col>
                <b-table id="case-table" ref="caseTable" show-empty sort-icon-left no-provider-sorting
                    empty-text="No Cases Found" empty-filtered-text="No Cases Found" :fields="tableFields"
                    :items="sortedItems" :per-page="pageSize" :current-page="pageNumber" :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc" :tbody-tr-class="colorCodeTable" selectable select-mode="single"
                    @row-selected="selectedRow" @sort-changed="handleSort">
                    <template #table-busy>
                        <div class="text-center my-2">
                            <b-spinner class="align-middle" />
                            <strong>Loading...</strong>
                        </div>
                    </template>
                    <template #cell(lock)="data">
                        <span v-if="data.item.lockedBy" v-b-tooltip.hover :title="`Locked by ${data.item.lockedBy}`">
                            <b-icon icon="lock-fill" variant="warning" />
                        </span>
                    </template>
                    <template #cell(details)="row">
                        <div class="table-icon-actions">
                            <a href="#" @click.prevent="selectCase(row.item)" title="View Details">
                                <b-icon icon="pencil-square"></b-icon>
                            </a>
                        </div>
                    </template>
                    <template #cell(lastName)="data">
                        <router-link :to="{ name: 'PatientDashboard', params: { id: data.item.patientId } }">
                            {{ data.value }}
                        </router-link>
                    </template>
                    <template #cell(assignment)="data">
                        <div class="badges" v-if="data.item.assignment">
                            <b-badge v-if="data.item.assignment.teamId" variant="primary" class="m-0"
                                :title="'Assigned to Team: ' + data.item.assignment.teamName">
                                <i class="fa fa-users" />
                                {{ data.item.assignment.teamName }}
                            </b-badge>
                            <b-badge v-if="data.item.assignment.userName" variant="info" class="m-0"
                                :title="'Assigned to User: ' + data.item.assignment.userName">
                                <i class="fa fa-user" />
                                {{ formatName(data.item.assignment.userName) }}
                            </b-badge>
                        </div>
                    </template>
                </b-table>

                <BottomPagerBar v-if="totalRows" v-model="pageNumber" :total-rows="totalRows" :filtered-rows="filteredRows" :per-page="pageSize"
                    show-total show-page-size-selector item-name="case" @change-page-size="pageSize = $event" />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <CaseDetailComponent :caseSelected="caseSelected" :show="show"
                    @updateCase="updateCaseList" @hidden="modalClosed" />
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { CaseDetail } from '@/models/Case';
import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';
import axios from 'axios';
import CaseDetailComponent from '@/components/Cases/CaseDetailComponent.vue';
import { BvTableFieldArray, BTable } from "bootstrap-vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { DatePicker } from "element-ui";
import moment from 'moment';
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import BottomPagerBar from '../BottomPagerBar.vue';

@Component({
    name: "CasesListComponent",
    components: {
        CaseDetailComponent,
        [DatePicker.name]: DatePicker,
        DateRangeFilter,
        BottomPagerBar
    }
})
export default class CasesListComponent extends Vue {

    @Prop() private show!: string;
    @Prop({ type: String, default: 'opened' }) private statusFilter!: string;
    @Prop({ type: Boolean, default: false }) private small!: boolean;
    @Prop({ type: Boolean, default: false }) private hidePatient!: boolean;
    @Prop({ type: Boolean, default: false }) private showStatus!: boolean;
    @Prop({ default: 50 }) private defaultPageSize!: number;
    @Prop() private defaultItems?: Array<CaseDetail>;
    @Prop({ type: String, default: undefined }) private initialCaseId?: string;

    @Ref("caseTable") private caseTable!: BTable;

    private caseSelected: CaseDetail | null = null;

    protected createdDateRangeFilter: { startDate: any, endDate: any } = { startDate: null, endDate: null };

    private pageNumber = 1;
    private pageSize = 50;

    private items: Array<CaseDetail> = [];

    private sortBy: string = 'created';
    private sortDesc: boolean = true;

    private problemFilter: string | null = null;
    private query: string | null = null;
    private assignmentFilter: string = 'all';

    private problems: string[] = [];
    private categories: any[] = [];

    get internalStatusFilter(): string {
        return this.statusFilter;
    }

    set internalStatusFilter(value: string) {
        this.$emit('update:statusFilter', value);
        if (value === 'all') {
            this.$emit('fetch-all');
        }
    }

    get statusOptions() {
        const hasAcceptCasesPermission = this.$permissions.some(
            p => p.permissionCodeName === 'Compliance/Accept Cases'
        );
        const hasAdminCompliancePermission = this.$permissions.some(
            p => p.permissionCodeName === 'Admin/Compliance'
        );

        const options = [
            { text: "Any Status", value: "all" },
            { text: "Opened", value: "opened" }
        ];

        // Only show "Resolved" option to users with Accept Cases permission
        if (hasAcceptCasesPermission) {
            options.push({ text: "Resolved", value: "resolved" });
        }

        // Only show "Closed" option to users with Admin/Compliance permission
        if (hasAdminCompliancePermission) {
            options.push({ text: "Closed", value: "completed" });
        }

        return options;
    }

    get problemOptions() {
        return [
            { text: "Any Type", value: null },
            ...this.problems
        ]
    }

    get assignmentFilterOptions() {
        return [
            { text: "Any Assignment", value: "all" },
            { text: "Assigned to Me or My Teams", value: "default" },
            { text: "Assigned to Me", value: "me" },
            { text: "Assigned to My Teams", value: "teams" },
            { text: "Unassigned", value: "unassigned" }
        ]
    }

    async getCaseProblems() {
        try {
            const resp = await axios.get<string[]>("/Case/problems");
            this.problems = resp.data;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getCaseCategories() {
        try {
            const resp = await axios.get<any[]>("/Case/categories");
            this.categories = resp.data;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    get startRowIndex() {
        return this.pageNumber * this.pageSize - this.pageSize;
    }

    get endRowIndex() {
        const calcIndex = this.startRowIndex + this.pageSize;
        if (calcIndex > this.filteredItems.length) {
            return this.filteredItems.length;
        }
        return calcIndex;
    }

    get totalRows() {
        return this.items.length;
    }

    get filteredRows() {
        return this.sortedItems.length;
    }

    created() {
        this.pageSize = this.defaultPageSize;
        this.getCaseProblems();
        this.getCaseCategories();
        this.getCases();
    }

    mounted() {
        if (this.initialCaseId) {
            this.loadInitialCase();
        }
    }

    async loadInitialCase() {
        if (!this.initialCaseId) return;

        try {
            const caseId = parseInt(this.initialCaseId);
            const resp = await axios.get<CaseDetail>(`/api/Case/${caseId}`);
            this.caseSelected = new CaseDetail(resp.data);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get tableFields(): BvTableFieldArray {
        return [
            { key: "lock", label: "" },
            {
                key: "id", label: this.small ? "Id" : "Case Id"
            },
            {
                key: "lastName",
                label: "Patient Name",
                formatter: (v: any, k: any, i: any) => i.lastName?.length < 1 ? `` : `${i.lastName}, ${i.firstName}`,
                sortable: true,
                visible: !this.hidePatient
            },
            {
                key: "dateOfBirth",
                formatter: (v: any) => new Date(v).toLocaleDateString(),
                sortable: true,
                visible: !this.hidePatient
            },
            {
                key: "addressState",
                label: "State",
                sortable: true,
                visible: !this.hidePatient
            },
            { key: "problem", label: "Type", sortable: true },
            {
                key: "categoryName",
                label: "Category",
                sortable: true,
                visible: this.categories.length > 0
            },
            {
                key: "assignment",
                label: "Assigned",
                sortable: true,
                visible: !this.small
            },
            {
                key: "nextTouch",
                label: "Next Touch",
                formatter: (v: any) => v ? moment.utc(v).local().format("l") : "",
                sortable: true,
                visible: !this.small
            },
            {
                key: "status",
                label: "Status",
                sortable: true,
                formatter: (v: any, k: any, i: any) => i?.accepted != null ? "Closed" : i?.resolved != null ? "Resolved" : "Opened",
                visible: this.showStatus,
            },
            { key: "created", formatter: (v: any) => v ? moment.utc(v).local().format("l LT") : "", sortable: true, visible: !this.small },
            { key: "resolved", formatter: (v: any) => v ? moment.utc(v).local().format("l LT") : "", sortable: true, visible: !this.small },
            {
                key: "accepted",
                label: "Closed",
                formatter: (v: any) => v ? moment.utc(v).local().format("l LT") : "",
                sortable: true, visible: !this.small
            },
            {
                key: "latest",
                label: "Status Date",
                formatter: (_: any, __: any, i: any) => {
                    if (i.accepted) return moment.utc(i.accepted).local().format("l LT");
                    if (i.resolved) return moment.utc(i.resolved).local().format("l LT");
                    return moment.utc(i.created).local().format("l LT");
                },
                visible: !!this.small
            },
            { key: "details", label: "", visible: !this.small, class: "details" },
        ].filter(i => i.visible !== false);
    }

    get filteredItems() {
        let items = this.items;

        if (this.problemFilter) {
            items = items.filter(i => i.problem === this.problemFilter);
        }

        if (this.createdDateRangeFilter?.startDate) {
            items = items.filter(i => new Date(i.created) >= moment(this.createdDateRangeFilter!.startDate).toDate() &&
                new Date(i.created) < moment(this.createdDateRangeFilter!.endDate).add(1, "day").toDate());
        }

        if (this.query) {
            const queryTerms = this.query.toLowerCase().split(" ");
            items = items.filter(i => queryTerms.every(qt =>
                (i.lastName || '').toLowerCase().includes(qt) ||
                (i.firstName || '').toLowerCase().includes(qt) ||
                (i.dateOfBirth ? new Date(i.dateOfBirth).toLocaleDateString().includes(qt) : false) ||
                i.id.toString().includes(qt)));
        }

        return items;
    }

    get sortedItems() {
        const items = [...this.filteredItems];

        if (!this.sortBy) return items;

        items.sort((a: CaseDetail, b: CaseDetail) => {
            let aVal: any = (a as any)[this.sortBy];
            let bVal: any = (b as any)[this.sortBy];

            // Handle nested assignment field for team/user sorting
            if (this.sortBy === 'assignment') {
                aVal = a.assignment?.userName || a.assignment?.teamName || '';
                bVal = b.assignment?.userName || b.assignment?.teamName || '';
            }

            // Handle date fields
            if (['created', 'resolved', 'accepted', 'dateOfBirth', 'nextTouch'].includes(this.sortBy)) {
                const aDate = aVal ? new Date(aVal).getTime() : 0;
                const bDate = bVal ? new Date(bVal).getTime() : 0;
                return aDate - bDate;
            }

            // Handle numeric fields
            if (this.sortBy === 'id') {
                return (aVal || 0) - (bVal || 0);
            }

            // Handle string fields (null-safe)
            aVal = String(aVal || '').toLowerCase();
            bVal = String(bVal || '').toLowerCase();
            return aVal.localeCompare(bVal);
        });

        if (this.sortDesc) items.reverse();

        return items;
    }

    async updateCaseList() {
        this.$emit("update");
        await this.getCases();
    }

    @Watch("defaultItems")
    @Watch("show")
    @Watch("statusFilter")
    @Watch("assignmentFilter")
    async getCases() {
        this.caseSelected = null;

        // CRITICAL: Use defaultItems if provided (for patient-scoped case lists)
        // This ensures we never fall back to fetching ALL cases when displaying patient-specific lists
        if (this.defaultItems) {
            this.items = this.defaultItems;
            return;
        }

        // Use statusFilter if available, otherwise fall back to show prop for backwards compatibility
        const status = this.statusFilter || this.show;

        if (!status) return;

        // Build query parameters based on assignment filter
        const params: any = {};

        if (this.assignmentFilter === 'me') {
            params.assignedToMe = true;
        } else if (this.assignmentFilter === 'teams') {
            params.assignedToMyTeams = true;
        } else if (this.assignmentFilter === 'unassigned') {
            params.unassigned = true;
        }
        // 'all' and 'default' don't set any params
        // 'default' lets backend apply default filter (assigned to me or my teams)
        // 'all' needs backend to show all cases - we'll use a special assignedUserName value
        if (this.assignmentFilter === 'all') {
            // Use special sentinel value to signal "all cases"
            params.assignedUserName = '*';
        }

        // If status is 'all', fetch all available statuses based on permissions and combine them
        if (status === 'all') {
            try {
                const hasAcceptCasesPermission = this.$permissions.some(
                    p => p.permissionCodeName === 'Compliance/Accept Cases'
                );
                const hasAdminCompliancePermission = this.$permissions.some(
                    p => p.permissionCodeName === 'Admin/Compliance'
                );

                // Always fetch opened cases
                const requests = [
                    axios.get<CaseDetail[]>('/api/Case/opened', { params })
                ];

                // Only fetch resolved cases if user has Accept Cases permission
                if (hasAcceptCasesPermission) {
                    requests.push(axios.get<CaseDetail[]>('/api/Case/resolved', { params }));
                }

                // Only fetch completed cases if user has Admin/Compliance permission
                if (hasAdminCompliancePermission) {
                    requests.push(axios.get<CaseDetail[]>('/api/Case/completed', { params }));
                }

                const responses = await Promise.all(requests);

                this.items = responses.flatMap(resp => resp.data.map(i => new CaseDetail(i)));
                return;
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
                return [];
            }
        }

        // For specific status, fetch from that endpoint
        const uri = `/api/Case/${status}`;

        try {
            const resp = await axios.get<CaseDetail[]>(uri, { params });
            this.items = resp.data.map(i => new CaseDetail(i));
            return resp.data.slice(this.startRowIndex, this.endRowIndex);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            return [];
        }
    }

    selectCase(item: CaseDetail) {
        this.caseSelected = new CaseDetail(item);
    }

    colorCodeTable(c: CaseDetail) {
        if (!this.showStatus) return "";
        if (!c?.accepted) return "table-warning";
        return "";
    }

    selectedRow(items: Array<any>) {
        if (!items.length) return;
        this.selectCase(items[0]);
    }

    modalClosed() {
        this.caseTable.clearSelected();
        this.caseSelected = null;
    }

    formatName(name: string) {
        if (name.includes("/")) return name.split("/")[1];
        if (name.includes("\\")) return name.split("\\")[1];
        return name.split("@")[0];
    }

    handleSort(event: { sortBy: string; sortDesc: boolean }) {
        this.sortBy = event.sortBy;
        this.sortDesc = event.sortDesc;
        this.pageNumber = 1;
    }
}
</script>

<style scoped>
.filter-grid {
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 430px), 1fr));
}

.small ::v-deep(table) {
    font-size: .98em;
}

.small .filter-grid {
    grid-template-columns: 1fr;
}

::v-deep(.details) {
    width: 50px;
}

.badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

@media (min-width: 1600px) {
    .filter-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}
</style>
